import { useEffect, useState } from 'react'
import { Alert, Group, Text, Loader } from '@mantine/core'
import { fetchFinopsHealth, type FinopsHealth, type FinopsHealthState } from '@/api/awsRoleCoverage'

// ============================================================
// FinopsDetectionHealthCard — card de saúde da detecção FinOps
//
// Consome GET /api/v1/admin/aws-access/coverage/finops-health.
// Fail-safe: se o fetch falhar, exibe o estado "indisponível"
// sem crashar a página pai.
// ============================================================

interface StateConfig {
  color: string
  label: string
  description: string
}

const STATE_CONFIG: Record<FinopsHealthState, StateConfig> = {
  ok: {
    color: 'green',
    label: 'Detecção saudável',
    description: 'O scanner de actions FinOps está rodando e produzindo dados recentes.',
  },
  falha: {
    color: 'red',
    label: 'Detecção roda mas não produz (items=0, errors>0)',
    description: 'O scanner executou mas não gerou resultados — verifique os erros de acesso.',
  },
  stale: {
    color: 'red',
    label: 'Detecção parada há muito tempo',
    description: 'Não há execuções recentes do scanner FinOps. Verifique o agendamento.',
  },
  sem_dados: {
    color: 'gray',
    label: 'Detecção ainda não reportou',
    description: 'Nenhum resultado foi registrado ainda — aguarde a primeira execução.',
  },
  indisponivel: {
    color: 'gray',
    label: 'Saúde indisponível',
    description: 'Não foi possível obter o estado da detecção FinOps.',
  },
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pt-BR')
}

export function FinopsDetectionHealthCard() {
  const [health, setHealth] = useState<FinopsHealth | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    fetchFinopsHealth()
      .then((data) => {
        if (alive) setHealth(data)
      })
      .catch(() => {
        // Fail-safe: exibe estado indisponivel sem crashar.
        if (alive) {
          setHealth({
            state: 'indisponivel',
            items: null,
            errors: null,
            assume_role_failures: null,
            access_denied: null,
            last_run: null,
          })
        }
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [])

  if (loading) {
    return (
      <Group gap="xs" data-testid="finops-health-card-loading">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">
          Verificando saúde da detecção FinOps…
        </Text>
      </Group>
    )
  }

  const state = health?.state ?? 'indisponivel'
  const cfg = STATE_CONFIG[state]

  return (
    <Alert
      color={cfg.color}
      title={cfg.label}
      data-testid="finops-health-card"
      data-health-state={state}
    >
      <Text size="sm" mb="xs">
        {cfg.description}
      </Text>
      <Group gap="xl" wrap="wrap">
        {health?.items != null && (
          <Text size="xs" c="dimmed" data-testid="finops-health-items">
            Items: <b>{health.items}</b>
          </Text>
        )}
        {health?.errors != null && (
          <Text size="xs" c="dimmed" data-testid="finops-health-errors">
            Erros: <b>{health.errors}</b>
          </Text>
        )}
        {health?.assume_role_failures != null && (
          <Text size="xs" c="dimmed" data-testid="finops-health-assume-role-failures">
            AssumeRole falhos: <b>{health.assume_role_failures}</b>
          </Text>
        )}
        {health?.access_denied != null && (
          <Text size="xs" c="dimmed" data-testid="finops-health-access-denied">
            Acesso negado: <b>{health.access_denied}</b>
          </Text>
        )}
        <Text size="xs" c="dimmed" data-testid="finops-health-last-run">
          Última execução: <b>{fmtDateTime(health?.last_run ?? null)}</b>
        </Text>
      </Group>
    </Alert>
  )
}

export default FinopsDetectionHealthCard
